import { getPlannerSnapshot } from '../features/plannerBasic/utils/plannerSnapshot';
import {
  APP_SCHEMA_VERSION,
  buildBlankTemplateData,
  buildBlankTemplateExport
} from './templateUtils';

const REQUIRED_TAIL_ARRAYS = ['items', 'cardItems', 'stackedCardItems', 'accordionItems', 'questions'];

const sanitizeFilename = (value) => {
  const base = (value || '').toString().trim() || 'BOPPPS_Template';
  return base.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
};

const downloadJson = (payload, filename) => {
  const dataStr = JSON.stringify(payload, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const ensureTailArrays = (block) => REQUIRED_TAIL_ARRAYS.reduce((acc, key) => {
  acc[key] = Array.isArray(block[key]) ? block[key] : [];
  return acc;
}, {});

const convertTableBlock = (block) => {
  const baseId = block.id || null;
  const columnId = (suffix) => (baseId ? `${baseId}-${suffix}` : undefined);
  return {
    ...block,
    type: 'multi-column-cards',
    content: '',
    columns: [
      {
        id: columnId('col-001'),
        title: 'Item',
        width: '33%',
        widthPercent: 33,
        cards: [
          {
            id: columnId('card-001'),
            title: 'Item Name',
            content: '<p>Describe the item.</p>',
            backgroundColor: '#eff6ff',
            textColor: '#1e3a8a',
            borderColor: '#3b82f6',
            showBorder: true
          }
        ]
      },
      {
        id: columnId('col-002'),
        title: 'Purpose',
        width: '33%',
        widthPercent: 33,
        cards: [
          {
            id: columnId('card-002'),
            title: 'Purpose',
            content: '<p>Explain the purpose.</p>',
            backgroundColor: '#f0fdf4',
            textColor: '#14532d',
            borderColor: '#22c55e',
            showBorder: true
          }
        ]
      },
      {
        id: columnId('col-003'),
        title: 'How to Use',
        width: '34%',
        widthPercent: 34,
        cards: [
          {
            id: columnId('card-003'),
            title: 'How to Use',
            content: '<p>Explain how to use it.</p>',
            backgroundColor: '#fff7ed',
            textColor: '#9a3412',
            borderColor: '#fb923c',
            showBorder: true
          }
        ]
      }
    ],
    ...ensureTailArrays(block)
  };
};

const replaceTablesInSections = (sections) => {
  if (!sections || typeof sections !== 'object') return sections;
  if (Array.isArray(sections)) {
    return sections.map((section) => ({
      ...section,
      blocks: (section.blocks || []).map((block) => {
        if (block?.type === 'table') {
          return convertTableBlock(block);
        }
        return { ...block, ...ensureTailArrays(block) };
      })
    }));
  }
  return Object.entries(sections).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = value.map((block) => {
        if (block?.type === 'table') {
          return convertTableBlock(block);
        }
        return { ...block, ...ensureTailArrays(block) };
      });
      return acc;
    }
    if (value && typeof value === 'object') {
      acc[key] = {
        ...value,
        blocks: (value.blocks || []).map((block) => {
          if (block?.type === 'table') {
            return convertTableBlock(block);
          }
          return { ...block, ...ensureTailArrays(block) };
        })
      };
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

const normalizePlannerData = (plannerData = {}) => ({
  lessonTopic: plannerData.lessonTopic || '',
  bridgeIn: plannerData.bridgeIn || '',
  objective: plannerData.objective || '',
  preAssessment: plannerData.preAssessment || '',
  postAssessment: plannerData.postAssessment || '',
  summary: plannerData.summary || '',
  reflections: plannerData.reflections || '',
  activities: Array.isArray(plannerData.activities) ? plannerData.activities : []
});

const buildActivitySummary = (activity = {}) => {
  const parts = [];
  if (activity.title) {
    parts.push(activity.title);
  }
  if (activity.facilitatorActivity) {
    parts.push(activity.facilitatorActivity);
  }
  if (activity.learnerActivity) {
    parts.push(`Students: ${activity.learnerActivity}`);
  }
  if (activity.materialsRequired) {
    parts.push(`Materials: ${activity.materialsRequired}`);
  }
  if (activity.learnerMaterials) {
    parts.push(`Student materials: ${activity.learnerMaterials}`);
  }
  if (activity.duration) {
    parts.push(`Duration: ${activity.duration}`);
  }
  return parts.join(' | ');
};

const buildCompanionPack = (plannerData) => {
  const normalized = normalizePlannerData(plannerData);
  const activitySummaries = normalized.activities
    .map(buildActivitySummary)
    .filter(Boolean);

  return {
    format: 'boppps_companion_pack',
    schema_version: '1.0.0',
    producer: 'TemplateManager',
    template_export_type: 'blank_template_for_llm',
    lesson_meta: {
      courseTopic: normalized.lessonTopic || '',
      topic: normalized.lessonTopic || '',
      duration_minutes_total: '',
      audience: '',
      prerequisites: '',
      software_context: ''
    },
    hard_rules: {
      do_not_modify_template_structure: true,
      do_not_change_ids_or_types: true,
      do_not_add_remove_reorder_blocks: true,
      only_fill_empty_strings: true,
      no_new_block_types: true,
      no_table_blocks: true,
      citations_foundational_only: true,
      do_not_invent_statistics: true,
      student_facing_voice: true
    },
    timing_plan: {
      bridge_in_min: '',
      outcomes_min: '',
      pre_assessment_min: '',
      participatory_learning_min: '',
      post_assessment_min: '',
      summary_min: ''
    },
    content_outline: {
      bridge_in: {
        hook: normalized.bridgeIn,
        connect_prior: '',
        objective_preview: ''
      },
      outcomes: normalized.objective ? [normalized.objective] : [],
      pre_assessment: normalized.preAssessment ? [normalized.preAssessment] : [],
      participatory_learning: {
        activity_1: activitySummaries[0] || '',
        activity_2: activitySummaries[1] || '',
        activity_3: activitySummaries[2] || ''
      },
      post_assessment: normalized.postAssessment ? [normalized.postAssessment] : [],
      summary: normalized.summary ? [normalized.summary] : [],
      resources: normalized.reflections ? [normalized.reflections] : []
    },
    block_plan: [
      { section: 'bridge-in', match: { type: 'headline', occurrence: 1 }, content_key: 'bridge_in.hook' },
      { section: 'bridge-in', match: { type: 'text', occurrence: 1 }, content_key: 'bridge_in.connect_prior' },
      { section: 'bridge-in', match: { type: 'text', occurrence: 2 }, content_key: 'bridge_in.objective_preview' },
      { section: 'outcomes', match: { type: 'list', occurrence: 1 }, content_key: 'outcomes' },
      { section: 'pre-assessment', match: { type: 'text', occurrence: 1 }, content_key: 'pre_assessment.0' },
      { section: 'participatory-learning', match: { type: 'stacked-cards', occurrence: 1 }, content_key: 'participatory_learning' },
      { section: 'post-assessment', match: { type: 'text', occurrence: 1 }, content_key: 'post_assessment.0' },
      { section: 'summary', match: { type: 'list', occurrence: 1 }, content_key: 'summary' },
      { section: 'resources', match: { type: 'cards', occurrence: 1 }, content_key: 'resources' }
    ],
    template_known_issues: {
      notes: 'Table blocks are unsupported and should be converted to multi-column-cards or stacked-cards.'
    }
  };
};

// From memory, not from browser storage: the planner keeps nothing on the
// machine. See features/plannerBasic/utils/plannerSnapshot.js.
const loadPlannerDataFromStorage = () => getPlannerSnapshot();

export const exportBlankTemplateWithCompanionPack = ({
  getLessonData,
  templateName,
  templateDescription,
  getCompanionContent
}) => {
  if (typeof getLessonData !== 'function') {
    throw new Error('Missing lesson data source for export.');
  }

  const templateJson = buildBlankTemplateData(getLessonData());
  const sanitizedTemplate = {
    ...templateJson,
    sections: replaceTablesInSections(templateJson.sections)
  };

  const payload = buildBlankTemplateExport({
    templateJson: sanitizedTemplate,
    templateName: templateName?.trim() || 'BOPPPS Template',
    description: templateDescription?.trim(),
    schemaVersion: APP_SCHEMA_VERSION
  });

  const filenameBase = sanitizeFilename(
    payload.exportFileName || payload.slideDeckName || templateName?.trim() || 'BOPPPS_Template'
  );

  const companionContent = typeof getCompanionContent === 'function'
    ? getCompanionContent()
    : loadPlannerDataFromStorage();

  downloadJson(payload, `${filenameBase}_BlankTemplate_LLM.json`);
  downloadJson(buildCompanionPack(companionContent), `${filenameBase}_Companion_ContentPack.json`);

  return {
    templateFilename: `${filenameBase}_BlankTemplate_LLM.json`,
    companionFilename: `${filenameBase}_Companion_ContentPack.json`
  };
};
